import { useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ConsultantTodoCard } from './ConsultantTodoCard';
import { isOverdue } from './todoDisplayUtils';
import { useSendGroupToClientMutation } from '@/hooks/useSendToClientMutation';
import type { TodoItem } from '@/types/todo';

// FIN-578 PR1 — read-only: same-type, same-audience rows collapse into one group.
// Expanding shows each item as its own full ConsultantTodoCard (own inline action),
// matching apps/client's TodoGroupCard expand shape — never checkboxes (Draft A,
// see specs/client-todos/screens.md).
//
// FIN-578 PR3 — F25: "Send N to client" wires the group-level bulk action. A full
// success shows no toast (the group shrinking/disappearing from the list is its own
// feedback, same as the single-item action); a partial batch (AC47/EC22 — a member
// concurrently resolved mid-batch) toasts "Sent X of Y" since the group merely shrinking
// wouldn't otherwise tell the consultant that not everyone made it. AC49: native
// `disabled` on the button, same reasoning as ConsultantTodoCard's single-item action.
export function ConsultantTodoGroupCard({
  todos,
  showCompanyLabel = false,
}: {
  todos: TodoItem[];
  showCompanyLabel?: boolean;
}) {
  const t = useTranslations('consultantTodos');
  const [expanded, setExpanded] = useState(false);
  const sendGroup = useSendGroupToClientMutation(todos[0]?.company_id);

  if (todos.length === 0) return null;

  const audience = todos[0].audience;
  const anyRequiredOverdue = todos.some((todo) => todo.required && isOverdue(todo.due_date));

  const handleSendGroup = async () => {
    const result = await sendGroup.mutateAsync(todos.map((todo) => todo.id));
    if (result.failedIds.length > 0) {
      toast(t('group.sentPartial', { sent: String(result.sentIds.length), total: String(todos.length) }), {
        description: t('group.sentPartialBody'),
      });
    }
  };

  return (
    <>
      <Card className={cn('rounded-lg', anyRequiredOverdue ? 'border-l-4 border-l-destructive shadow-md' : 'shadow-sm')}>
        <CardContent className="flex flex-col gap-2 p-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
              data-testid="consultant-todo-group-toggle"
            >
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-muted text-sm font-semibold">
                {todos.length}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{t('group.count', { count: todos.length })}</p>
                <p className="text-xs text-muted-foreground">{t('group.clickToSee')}</p>
              </div>
            </button>
            {showCompanyLabel && todos[0].companies?.name && (
              <Badge variant="secondary" data-testid="todo-company-badge">{todos[0].companies.name}</Badge>
            )}
            <Badge variant={audience === 'internal' ? 'outline' : 'default'}>
              {audience === 'internal' ? t('card.internal') : t('card.client')}
            </Badge>
            {audience === 'internal' && (
              <Button
                size="sm"
                className="rounded-full"
                disabled={sendGroup.isPending}
                onClick={handleSendGroup}
                data-testid="send-group-to-client-button"
              >
                {sendGroup.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                {t('group.sendGroup', { count: String(todos.length) })}
              </Button>
            )}
            <button
              type="button"
              className={cn('flex-none text-muted-foreground transition-transform', expanded && 'rotate-180')}
              onClick={() => setExpanded((e) => !e)}
              aria-label={expanded ? t('group.collapse') : t('group.expand')}
            >
              <ChevronDown size={16} />
            </button>
          </div>
        </CardContent>
      </Card>

      {expanded && (
        <div className="ml-4 mt-1 flex flex-col gap-2 border-l-2 pl-3">
          {todos.map((todo) => (
            <ConsultantTodoCard key={todo.id} todo={todo} showCompanyLabel={showCompanyLabel} />
          ))}
        </div>
      )}
    </>
  );
}
